import axios from 'axios';

// 创建 axios 实例
const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
});

// 请求拦截器：自动添加 JWT token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理 401 未授权错误
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Token 过期或无效，清除登录状态并跳转到登录页
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('isLoggedIn');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 文档相关的 API 调用
export const documentApi = {
  // 获取所有文档
  getAllDocuments(params = {}) {
    return api.get('/documents', { params });
  },

  // 根据 ID 获取文档
  getDocumentById(id) {
    return api.get(`/documents/${id}`);
  },

  // 创建文档
  createDocument(documentData, file) {
    const formData = new FormData();
    formData.append('document', new Blob([JSON.stringify(documentData)], { type: 'application/json' }));
    if (file) {
      formData.append('file', file);
    }

    return api.post('/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // 上传 PDF 前前端会先用 pdf-lib 嵌入二维码（解析+重新生成），大文件较耗时；
      // 后端写盘 + DB 落库也需要时间，默认 10s 超时易触发 ECONNABORTED 导致"上传失败"。
      timeout: 60000
    });
  },

  // 更新文档（不带文件）
  updateDocument(id, documentData) {
    return api.put(`/documents/${id}`, documentData);
  },

  // 更新文档（带文件）
  updateDocumentWithFile(id, documentData, file) {
    const formData = new FormData();
    formData.append('document', new Blob([JSON.stringify(documentData)], { type: 'application/json' }));
    if (file) {
      formData.append('file', file);
    }

    return api.put(`/documents/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // 同 createDocument，上传场景放宽到 60s。
      timeout: 60000
    });
  },

  // 删除文档
  deleteDocument(id) {
    return api.delete(`/documents/${id}`);
  },
    // 移除文档已关联的 PDF（编辑时删除文件）
    removePdf(id) {
      return api.delete(`/documents/${id}/pdf`);
    },

  // 预览 PDF (已修复中文文件名 400 错误)
  viewPdf(fileName) {
    return `${api.defaults.baseURL}/documents/view-pdf/${encodeURIComponent(fileName)}`;
  },

  // 下载 PDF (已修复中文文件名 400 错误)
  downloadPdf(fileName) {
    return `${api.defaults.baseURL}/documents/download-pdf/${encodeURIComponent(fileName)}`;
  },

  // 已登录用户预览 PDF（携带 token，返回 blob 响应）
  viewPdfBlob(fileName) {
    return api.get(`/documents/view-pdf/${encodeURIComponent(fileName)}`, { responseType: 'blob' });
  },

  // 公开报告核验：按报告编号查看 PDF（移动端公开接口，无需登录）
  viewPublicPdf(reportNumber) {
    return `${api.defaults.baseURL}/documents/public-pdf/${encodeURIComponent(reportNumber)}`;
  },

  // 根据报告编号查询（移动端公开接口）
  queryByReportNumber(reportNumber) {
    return api.get(`/documents/query/${encodeURIComponent(reportNumber)}`);
  },

  // 搜索文档（支持报告编号、样品名称、委托单位）
  searchDocuments(params) {
    return api.get('/documents/search', { params });
  }
};

export default api;